import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService, UserContext, LoginRequest } from '../services/authService';
import { TranslationCache } from '../i18n';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserContext | null;
  login: (request: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserContext | null>(null);

  useEffect(() => {
    const initAuth = () => {
      const isAuth = AuthService.isAuthenticated();
      setIsAuthenticated(isAuth);

      if (isAuth) {
        const userData = AuthService.getUserDataFromToken();
        if (userData) {
          // Restore tenantName and defaultWarehouse from localStorage (not stored in JWT)
          userData.tenantName = localStorage.getItem('bizclick_tenantName') || undefined;
          userData.defaultWarehouse = localStorage.getItem('bizclick_warehouse') || undefined;
        }
        setUser(userData);
      }
    };

    initAuth();
  }, []);

  const login = async (request: LoginRequest) => {
    const response = await AuthService.login(request);
    AuthService.setToken(response.token);

    // Store tenantName and defaultWarehouse separately (not in JWT token)
    localStorage.setItem('bizclick_tenantName', response.tenantName);
    if (response.defaultWarehouse) {
      localStorage.setItem('bizclick_warehouse', response.defaultWarehouse);
    }

    const userData = {
      companyNo: response.companyNo,
      customerNo: response.customerNo,
      operatorId: response.operatorId,
      operatorName: response.operatorName,
      operatorGroupId: response.operatorGroupId,
      tenantName: response.tenantName,
      defaultWarehouse: response.defaultWarehouse
    };

    setUser(userData);
    setIsAuthenticated(true);

    // Clear translation cache when user logs in to ensure fresh translations
    TranslationCache.clearCache();
  };

  const logout = async () => {
    await AuthService.logout();
    localStorage.removeItem('bizclick_tenantName');
    setIsAuthenticated(false);
    setUser(null);

    // Clear translation cache when user logs out
    TranslationCache.clearCache();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
